import { Api } from './api';
import { AutoOpen } from './auto-open';
import { ChatPanel } from './chat';
import { el, svgIcon, ICONS } from './dom';
import { EventBatcher, patchHistory } from './events';
import { Identity } from './identity';
import { WIDGET_CSS } from './styles';
import { SurveyEngine } from './surveys';
import { BootstrapData } from './types';
import { detectDevice, dlog, envInfo, pageUrl, setDebug, utmParams } from './util';

// Entry point (docs/widget.md §2). The single unbreakable rule: ANY init
// failure → the widget silently doesn't render. A broken widget must never
// break a customer's shop (decision §7-4).

const DEFAULT_API_BASE = 'https://api.chatbi.ir/api/v1'; // production; demo overrides via data-api-base
const DEFAULT_Z_INDEX = 2147482000;

// document.currentScript is only valid during the initial synchronous run — capture now
const ownScript =
  (document.currentScript as HTMLScriptElement | null) ??
  document.querySelector<HTMLScriptElement>('script[data-site-key]');

function onDomReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

async function init(): Promise<void> {
  const w = window as unknown as Record<string, unknown>;
  if (w.__chatbiLoaded) return; // double-include guard
  w.__chatbiLoaded = true;

  if (!ownScript) return;
  const siteKey = ownScript.getAttribute('data-site-key');
  if (!siteKey) return;

  const apiBase = (ownScript.getAttribute('data-api-base') ?? DEFAULT_API_BASE).replace(/\/$/, '');
  const zIndex = parseInt(ownScript.getAttribute('data-z-index') ?? '', 10) || DEFAULT_Z_INDEX;
  setDebug(ownScript.getAttribute('data-debug') === 'true');

  const api = new Api(apiBase, siteKey);
  const ids = new Identity(siteKey);

  // ---- bootstrap: identity + effective config in one round-trip ----
  const device = detectDevice();
  const boot = await api.post<BootstrapData>('/public/bootstrap', {
    anonId: ids.getAnonId(),
    pageUrl: pageUrl(),
    referrer: document.referrer ? document.referrer.slice(0, 2000) : undefined,
    ...device,
    ...utmParams(),
    ...envInfo(),
  });
  // merchant switched the widget off in settings → render nothing (the server
  // omits everything but this flag). A disabled widget must leave no trace.
  if (boot.botEnabled === false) {
    dlog(
      boot.disabledReason === 'trial_quota_exhausted'
        ? 'widget hidden because trial AI quota is exhausted'
        : 'widget disabled for this site — not rendering',
    );
    return;
  }

  ids.setAnonId(boot.anonId);
  dlog('bootstrapped', boot);

  // Defensive clamp: the backend validates 0..200, but a cached/legacy
  // bootstrap must never be able to position the widget off-screen.
  const marginHorizontal = clampMargin(boot.config.theme.marginHorizontal);
  const marginBottom = clampMargin(boot.config.theme.marginBottom);

  // ---- shadow DOM shell ----
  const hostEl = document.createElement('div');
  hostEl.setAttribute('data-chatbi', '');
  const shadow = hostEl.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = WIDGET_CSS;
  shadow.appendChild(styleEl);

  const root = el('div', {
    class: 'cb-root',
    attrs: {
      dir: boot.site.rtl ? 'rtl' : 'ltr',
      style: `--cb-primary: ${boot.config.theme.primaryColor}; --cb-z: ${zIndex}; --cb-margin-x: ${marginHorizontal}px; --cb-margin-y: ${marginBottom}px;`,
    },
  });
  shadow.appendChild(root);
  document.body.appendChild(hostEl);

  // ---- events ----
  const events = new EventBatcher(api, () => ids.getAnonId());
  events.start();
  events.track('page_view');

  // ---- launcher + chat panel ----
  const side =
    boot.config.theme.position === 'bottom-left' ? 'left: var(--cb-margin-x);' : 'right: var(--cb-margin-x);';
  const launcher = el(
    'button',
    {
      class: 'cb-launcher',
      // a manual open/close means the visitor is in control → stand down the auto-open
      attrs: { 'aria-label': boot.config.botName, type: 'button', style: side },
      on: {
        click: () => {
          autoOpen.suppress();
          panel.toggle();
        },
      },
    },
    [svgIcon(ICONS.chat, 26)],
  );
  const panel = new ChatPanel(root, boot, api, ids, events, () => launcher.focus());
  root.appendChild(launcher);

  // connect the realtime socket as soon as the widget mounts (not just on open) so the
  // visitor shows as online and an operator can pop the panel open to grab their attention
  panel.connectRealtime();

  // ---- merchant auto-open (settings page) ----
  const autoOpen = new AutoOpen(
    boot.config.autoOpen ?? { enabled: false, delaySeconds: 5, pages: [] },
    () => panel.open,
    () => panel.openPanel(),
    siteKey,
  );
  autoOpen.evaluate();

  // ---- surveys ----
  const surveys = new SurveyEngine(root, api, ids, events, boot.site.language, boot.config.theme.position);
  void surveys.refresh();

  // ---- SPA route changes: page_view + re-run survey eligibility + auto-open ----
  patchHistory(() => {
    events.track('page_view');
    void surveys.refresh();
    autoOpen.evaluate(); // a match on the new route can still trigger the auto-open
  });

  // tiny public handle (used by "manual" placements and our own demo/QA)
  w.ChatBI = {
    open: () => {
      autoOpen.suppress();
      panel.openPanel();
    },
    close: () => panel.close(),
    toggle: () => {
      autoOpen.suppress();
      panel.toggle();
    },
    anonId: () => ids.getAnonId(),
  };
}

function clampMargin(value: number | undefined): number {
  return Number.isFinite(value) ? Math.min(200, Math.max(0, Math.round(value!))) : 20;
}

try {
  onDomReady(() => {
    init().catch((err) => dlog('init failed — widget will not render', err));
  });
} catch {
  /* silent by design */
}
